import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionTitle from '@/components/base/SectionTitle';
import ComicCard from '@/components/base/ComicCard';
import { categories, categoryComics } from '@/mocks/categoryData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const category = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const filters = [
    { slug: 'all', label: t('category.filters.all') },
    { slug: 'romance', label: t('category.filters.romance') },
    { slug: 'fantasy', label: t('category.filters.fantasy') },
    { slug: 'action', label: t('category.filters.action') },
    { slug: 'thriller', label: t('category.filters.thriller') },
    { slug: 'comedy', label: t('category.filters.comedy') },
    { slug: 'drama', label: t('category.filters.drama') },
    { slug: 'daily', label: t('category.filters.daily') },
  ];

  const sort = searchParams.get('sort') || 'popular';
  const isPopular = sort === 'popular';

  const sortedComics = useMemo(() => {
    if (isPopular) {
      return [...categoryComics].sort((a, b) => {
        const numA = parseFloat(a.reads.replace(/[^0-9.]/g, '')) * (a.reads.includes('亿') ? 10000 : 1);
        const numB = parseFloat(b.reads.replace(/[^0-9.]/g, '')) * (b.reads.includes('亿') ? 10000 : 1);
        return numB - numA;
      });
    }
    return categoryComics;
  }, [isPopular]);

  const pageTitle = category
    ? `${category.name}韩漫 - 韩国漫画在线免费阅读_韩漫大全`
    : '分类浏览 - 韩国漫画在线免费阅读_韩漫大全';

  const canonicalUrl = `http://szshops.com/category/${slug || 'all'}`;

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageTitle,
    "url": canonicalUrl,
    "inLanguage": "zh-CN",
    "isPartOf": { "@type": "WebSite", "name": "韩漫大全", "url": "http://szshops.com/" },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://szshops.com/" },
        { "@type": "ListItem", "position": 2, "name": category ? category.name : "全部分类" }
      ]
    }
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={`韩漫大全分类浏览页面，按${category ? category.name : '分类'}筛选好看的韩国漫画，支持韩漫在线免费阅读，大全内容涵盖热门与精选作品。`} />
      <link rel="canonical" href={canonicalUrl} />
      <script type="application/ld+json">{JSON.stringify(ldJson)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.category'), to: '/category/all' },
                ...(category ? [{ label: category.name }] : []),
              ]}
            />

            <section className="px-6 md:px-10 py-10 md:py-14">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                  <div className="lg:w-[40%]">
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full border border-foreground-200 text-foreground-500 mb-4">
                      {t('category.title')}
                    </span>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-2">
                      {category ? category.name : '全部'}
                      <span className="text-secondary-500 font-light"> 韩漫</span>
                    </h1>
                    <p className="text-sm text-foreground-500 mt-4 mb-6 max-w-md leading-relaxed">
                      {t('category.subtitle')}
                      {category && ` 当前浏览「${category.name}」分类，共收录 ${category.count} 部作品。`}
                    </p>
                  </div>

                  <div className="lg:w-[60%] flex items-end justify-end">
                    <div className="flex gap-4 lg:gap-8 text-right">
                      <div>
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950">
                          {category ? category.count : '3000+'}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">{t('category.stats.totalWorks')}</p>
                      </div>
                      <div>
                        <p className="text-3xl md:text-4xl font-bold text-accent-600">2.3亿</p>
                        <p className="text-xs text-foreground-500 mt-1">{t('category.stats.totalReads')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="px-6 md:px-10 pb-8">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex items-center gap-1.5 overflow-x-auto pb-2 flex-wrap">
                  {filters.map((f) => (
                    <Link
                      key={f.slug}
                      to={`/category/${f.slug}${isPopular ? '?sort=popular' : ''}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        (slug || 'all') === f.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {f.label}
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            <section className="px-6 md:px-10 pb-16 md:pb-20">
              <div className="max-w-[1280px] mx-auto">
                <SectionTitle
                  title={category ? `「${category.name}」热门作品` : '全部热门作品'}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                  {sortedComics.map((comic, idx) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      cover={comic.cover}
                      rating={comic.rating}
                      reads={comic.reads}
                      status={comic.status}
                      genres={comic.genres}
                      updateDay={comic.updateDay}
                      featured={idx === 0}
                    />
                  ))}
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}